import * as Sentry from '@sentry/react';

import User from './model/General/User';

const parseSampleRate = (rawValue: string | undefined, fallback: number) => {
  const parsed = Number(rawValue);

  if (Number.isNaN(parsed)) {
    return fallback;
  }

  return Math.min(1, Math.max(0, parsed));
};

const SENTRY_CAPTURED_FLAG = '__acronexSentryCaptured';
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;
const createErrorFromUnknown = (error: unknown) => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  try {
    return new Error(JSON.stringify(error));
  } catch {
    return new Error(String(error));
  }
};

const markErrorAsCaptured = (error: unknown) => {
  if (!isRecord(error)) {
    return false;
  }

  if (error[SENTRY_CAPTURED_FLAG] === true) {
    return true;
  }

  error[SENTRY_CAPTURED_FLAG] = true;

  return false;
};

interface CapturedErrorContext {
  feature?: string;
  action?: string;
  level?: Sentry.SeverityLevel;
  fingerprint?: string[];
  tags?: Record<string, string | number | boolean | null | undefined>;
  contexts?: Record<string, Record<string, unknown>>;
  extra?: Record<string, unknown>;
}

interface ReactErrorInfo {
  componentStack?: string;
}

const SENTRY_APPLICATION_KEY = 'acronex-operaciones-frontend';
const REACT_CONSOLE_DUPLICATE_PATTERNS = [
  /The above error occurred in the <.+> component/i,
  /Consider adding an error boundary to your tree/i,
];
const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'x-csrf-token',
]);
const isReactConsoleDuplicateEvent = (event: Sentry.ErrorEvent) => {
  if (event.logger !== 'console') {
    return false;
  }

  const candidateValues = [
    event.message,
    ...(Array.isArray(event.extra?.arguments) ? event.extra.arguments : []),
  ];

  return candidateValues.some(value => {
    if (typeof value !== 'string') {
      return false;
    }

    return REACT_CONSOLE_DUPLICATE_PATTERNS.some(pattern => pattern.test(value));
  });
};

const sanitizeRequestHeaders = (headers: unknown) => {
  if (!isRecord(headers)) {
    return headers;
  }

  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => {
      if (SENSITIVE_HEADERS.has(key.toLowerCase())) {
        return [key, '[redacted]'];
      }

      return [key, value];
    }),
  );
};

const sanitizeEvent = (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
  if (isReactConsoleDuplicateEvent(event)) {
    return null;
  }

  if (event.request?.headers) {
    event.request = {
      ...event.request,
      headers: sanitizeRequestHeaders(event.request.headers) as Record<string, string>,
    };
  }

  return event;
};

interface HttpErrorPolicyContext {
  statusCode?: number;
  axiosCode?: string;
}

interface HttpErrorPolicy {
  level: Sentry.SeverityLevel;
  category: 'network' | 'http.4xx' | 'http.5xx' | 'http.unknown';
  expected: boolean;
}

/**
 * metadata:
 * - feature: observability.sentry-policy
 * - purpose: unificar el criterio de severidad para errores HTTP reportados a Sentry
 */
export const getHttpErrorPolicy = ({
  statusCode,
  axiosCode,
}: HttpErrorPolicyContext): HttpErrorPolicy => {
  if (axiosCode === 'ERR_NETWORK') {
    return {
      level: 'error',
      category: 'network',
      expected: false,
    };
  }

  if (typeof statusCode === 'number' && statusCode >= 500) {
    return {
      level: 'error',
      category: 'http.5xx',
      expected: false,
    };
  }

  if (typeof statusCode === 'number' && statusCode >= 400) {
    return {
      level: 'warning',
      category: 'http.4xx',
      expected: true,
    };
  }

  return {
    level: 'error',
    category: 'http.unknown',
    expected: false,
  };
};

/**
 * metadata:
 * - feature: observability.sentry
 * - purpose: inicializar el SDK de Sentry en frontend de manera controlada por entorno
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  const thirdPartyErrorFilterIntegration: ReturnType<
    typeof Sentry.captureConsoleIntegration
  > | null =
    'thirdPartyErrorFilterIntegration' in Sentry
      ? (
          Sentry as typeof Sentry & {
            thirdPartyErrorFilterIntegration: (options: {
              filterKeys: string[];
              behaviour:
                | 'drop-error-if-contains-third-party-frames'
                | 'drop-error-if-exclusively-contains-third-party-frames'
                | 'apply-tag-if-contains-third-party-frames'
                | 'apply-tag-if-exclusively-contains-third-party-frames';
            }) => ReturnType<typeof Sentry.captureConsoleIntegration>;
          }
        ).thirdPartyErrorFilterIntegration({
          filterKeys: [SENTRY_APPLICATION_KEY],
          behaviour: 'apply-tag-if-contains-third-party-frames',
        })
      : null;

  // Evita inicializar el SDK cuando el DSN todavia no fue configurado.
  if (!dsn) {
    return;
  }

  const sentryInDevEnabled = import.meta.env.VITE_SENTRY_ENABLE_IN_DEV === 'true';

  Sentry.init({
    dsn,
    enabled: !import.meta.env.DEV || sentryInDevEnabled,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    // Adjunta stacktrace a eventos basados en mensajes, por ejemplo console.error sin Error.
    attachStacktrace: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Captura console.error como eventos para trazabilidad de fallos reportados en consola.
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      // Etiqueta frames de terceros para separar ruido externo del codigo propio.
      ...(thirdPartyErrorFilterIntegration ? [thirdPartyErrorFilterIntegration] : []),
    ],
    // Filtra duplicados conocidos de React y redacta headers sensibles antes de enviar.
    beforeSend: sanitizeEvent,
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1),
    sendDefaultPii: false,
  });
};

/**
 * metadata:
 * - feature: observability.sentry-user
 * - purpose: asociar identidad del usuario autenticado a los eventos de error
 */
export const setSentryUserContext = (user?: User) => {
  if (!user) {
    Sentry.setUser(null);

    return;
  }

  Sentry.setUser({
    id: user.id ? String(user.id) : undefined,
    username: user.fullUsername || user.username || undefined,
    email: user.email || undefined,
  });

  if (user.workArea) {
    Sentry.setTag('user.work_area', String(user.workArea));
  }

  if (user.companyData?.id) {
    Sentry.setTag('user.company_id', String(user.companyData.id));
  }
};

/**
 * metadata:
 * - feature: observability.sentry-user
 * - purpose: limpiar el usuario de Sentry al cerrar sesion o perder autenticacion
 */
export const clearSentryUserContext = () => {
  Sentry.setUser(null);
};

/**
 * metadata:
 * - feature: observability.sentry-handled-errors
 * - purpose: registrar errores manejados (catch) para mantener trazabilidad
 */
export const captureHandledError = (error: unknown, context?: CapturedErrorContext) => {
  if (markErrorAsCaptured(error)) {
    return;
  }

  const parsedError = createErrorFromUnknown(error);

  Sentry.withScope(scope => {
    scope.setLevel(context?.level || 'error');
    scope.setTag('error.handled', 'true');

    if (context?.feature) {
      scope.setTag('error.feature', context.feature);
    }

    if (context?.action) {
      scope.setTag('error.action', context.action);
    }

    if (context?.fingerprint?.length) {
      scope.setFingerprint(context.fingerprint);
    }

    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          scope.setTag(key, String(value));
        }
      });
    }

    if (context?.contexts) {
      Object.entries(context.contexts).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
    }

    if (context?.extra) {
      scope.setExtras(context.extra);
    }

    Sentry.captureException(parsedError);
  });
};

/**
 * metadata:
 * - feature: observability.sentry-react
 * - purpose: registrar errores de render de React con componentStack para mejorar el debugging
 */
export const captureReactRenderError = (error: unknown, errorInfo?: ReactErrorInfo) => {
  captureHandledError(error, {
    feature: 'react',
    action: 'error-boundary',
    tags: {
      'error.source': 'react.error_boundary',
    },
    contexts: errorInfo?.componentStack
      ? {
          react: {
            componentStack: errorInfo.componentStack,
          },
        }
      : undefined,
  });
};
